import { selectMatches } from "selectors/Match";
import { MessageBox } from "styles/text";
import { anyStringContains, nonUndefined, onlyUnique } from "Utility";
import { StatTable } from "components/StatsTable/StatTable";
import {
  selectRosterOrgDetails,
  selectRosterScope,
  selectTournamentDriverName,
} from "selectors/Roster";
import { getSharedDriverTournamentStats } from "stats/Tournament/Shared";
import { getSharedDriverTournamentStatTableColumns } from "components/StatsTable/ColumnConfigs/TournamentDriver/Shared";
import { matchContainsDriver } from "model/Match/Match";
import { roundIncludesDriver } from "model/Match/Track/TrackRound";
import { getRosterDriverIds, getRosterId, Roster } from "model/Roster/Roster";
import { useAppSelector } from "app/hooks";
import { RatingType } from "stats/Rating/RatingTypes";
import { getDriverRatings } from "stats/Rating/DriverRating";
import {
  getMatchesRounds,
  getMatchesTrackRoundsById,
  getMatchesTrackTimesById,
} from "model/Match/Matches";
import { shallowEqual } from "react-redux";

interface Props {
  tournamentId: string;
  matchIds: string[];
  priorMatchIds: string[];
  ratingType: RatingType;
  trackIds?: string[];
  primaryColor?: string;
  secondaryColor?: string;
}

export const TournamentDriverStats = ({
  tournamentId,
  matchIds,
  priorMatchIds,
  ratingType,
  trackIds,
  primaryColor,
  secondaryColor,
}: Props) => {
  const rosterScope = useAppSelector((state) =>
    selectRosterScope(state, tournamentId)
  );

  const matches = useAppSelector(
    (state) => selectMatches(state, matchIds),
    shallowEqual
  );
  const priorMatches = useAppSelector(
    (state) => selectMatches(state, priorMatchIds),
    shallowEqual
  );
  const allMatches = matches.concat(priorMatches).filter(onlyUnique);
  const calculateDriverRatings = allMatches.every((m) => m.isComplete);

  const driverIds = matches
    .flatMap((m) =>
      m.teams.rosters.flatMap((r) => getRosterDriverIds(r, m.teams.scope) ?? [])
    )
    .filter(onlyUnique);

  const allTimes = getMatchesTrackTimesById(allMatches, trackIds);
  const evaluatedRounds = getMatchesTrackRoundsById(matches, trackIds);
  const driverRatings = calculateDriverRatings
    ? getDriverRatings(driverIds, allTimes, evaluatedRounds)
    : undefined;

  const driverStats = nonUndefined(
    useAppSelector((state) =>
      driverIds.map((driverId) => {
        const driverMatches = matches
          .filter((m) => matchContainsDriver(m, driverId))
          .sort((a, b) => b.startTimeMs - a.startTimeMs);
        if (driverMatches.length === 0) {
          return undefined;
        }
        // todo - bad
        const roster = (driverMatches[0].teams.rosters as Roster[]).find((r) =>
          (!!rosterScope
            ? getRosterDriverIds(r, rosterScope) ?? []
            : []
          ).includes(driverId)
        );
        if (!roster) {
          return undefined;
        }

        const rosterId = getRosterId(roster);
        if (!rosterId) {
          return undefined;
        }
        const orgDetails = selectRosterOrgDetails(
          state,
          rosterId,
          tournamentId
        );
        const driverName = selectTournamentDriverName(
          state,
          driverId,
          tournamentId
        );
        if (!driverName) {
          return undefined;
        }

        const roundsWithDriver = getMatchesRounds(
          driverMatches,
          trackIds
        ).filter((r) => roundIncludesDriver(r, driverId));

        if (roundsWithDriver.length === 0) {
          return undefined;
        }

        const driverRating = driverRatings?.[driverId];

        return getSharedDriverTournamentStats(
          driverId,
          orgDetails?.id,
          orgDetails?.name,
          driverName,
          driverRating,
          roundsWithDriver
        );
      })
    )
  );

  return (
    <>
      <MessageBox style={{ margin: "10px" }}>
        Driver Rating - Calculated from a combination of a driver's race times
        and AF%. DRs are shown after all relevant current and previous matches
        are completed.
        <br />
        Average Finish Percentile - Calculated from a driver's finish positions
        relative to the total number of players in each round. (100% - always
        first, 0% - always last)
      </MessageBox>

      <StatTable
        thinner={true}
        columns={getSharedDriverTournamentStatTableColumns(ratingType)}
        stats={driverStats}
        keyGetter={(arg) => arg.driverName}
        initialSortColumnIdx={1}
        initialSortOrder="DESC"
        paginationAmount={20}
        searchFunction={(arg, searchText) =>
          anyStringContains(searchText, arg.driverName, arg.orgName)
        }
        searchPlaceholder={"Filter: Team or Driver. Comma for multiple"}
        primaryColor={primaryColor}
        secondaryColor={secondaryColor}
      />
    </>
  );
};
